import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "~/lib/cn";

/**
 * «La palabra que espera» (T297 redesigned 2026-07-27, absorbed T373) —
 * the library-nav unlock signal: the word «nueva»/«new» in serif italic
 * superscript, plus the periodic champagne glint the anchor contexts put
 * on the link label (`.lib-link-shimmer`). Champán
 * (`--color-tool*`), not the ritual magenta: it reads as "a new piece waits
 * in your vitrine", calm, no count, no urgency.
 *
 * Arrival/breath/discharge choreography is pure CSS; reduced-motion
 * neutralises all of it (see app.css).
 */
export function LibSeal({ clearing = false }: { clearing?: boolean }) {
  const { t } = useTranslation("nav");
  return (
    <span
      className={cn("lib-seal-word", clearing && "is-clearing")}
      role="status"
      aria-label={t("seal.new_technique")}
    >
      {t("seal.new_short")}
    </span>
  );
}

/** Discharge beat — 460 ms: 360 ms (the word's exit fade, the longest exit
 *  animation) + 100 ms of render-flush headroom so the CSS finishes before
 *  the node is removed. Revisit the headroom if exit durations change. */
const DISCHARGE_MS = 460;

/**
 * Presence with a farewell (T307). While `armed` the signal is mounted;
 * when it disarms (the library visit witnessed it, T355) the word stays
 * mounted for one discharge beat with `clearing: true`, then unmounts — it
 * fades out instead of popping out.
 *
 * Reduced-motion contract (R-T307-2): no flash, immediate removal.
 * The store stays synchronous; this phase is presentation-only.
 */
export function useSealPresence(armed: boolean): { mounted: boolean; clearing: boolean } {
  const [clearing, setClearing] = useState(false);
  const [mounted, setMounted] = useState(armed);
  const prevArmed = useRef(armed);

  useEffect(() => {
    const wasArmed = prevArmed.current;
    prevArmed.current = armed;
    if (armed) {
      setMounted(true);
      setClearing(false);
      return;
    }
    if (!wasArmed) {
      setMounted(false);
      return;
    }
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      setMounted(false);
      return;
    }
    setClearing(true);
    const id = window.setTimeout(() => {
      setMounted(false);
      setClearing(false);
    }, DISCHARGE_MS);
    return () => window.clearTimeout(id);
  }, [armed]);

  return { mounted, clearing };
}
